// src/components/ReadjudicacionMasiva/CrearLote/BuscadorAbogados.js

import React, { useState, useEffect, useRef } from 'react';
import { TextField, CircularProgress, Box, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import { readjBuscarAbogadosJWT } from '../../../api/endpoints';

const BuscadorAbogados = ({ value, onChange, label, placeholder }) => {
    const [opciones, setOpciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef(null);

    // Sincronizar inputValue con value externo
    useEffect(() => {
        if (typeof value === 'object' && value !== null) {
            setInputValue(value.NombreCompleto || '');
        } else if (typeof value === 'string') {
            setInputValue(value || '');
        } else if (!value) {
            setInputValue('');
        }
    }, [value]);

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if ((inputValue || '').trim().length < 3) {
            setOpciones([]);
            setOpen(false);
            return;
        }

        timeoutRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await readjBuscarAbogadosJWT(inputValue);

                const items = response.ok && Array.isArray(response.abogados) ? response.abogados : [];
                setOpciones(items);
                setOpen(items.length > 0);
            } catch (err) {
                console.error('Error al buscar abogados:', err);
                setOpciones([]);
                setOpen(false);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [inputValue]);

    return (
        <Autocomplete
            open={open}
            onOpen={() => { if (opciones.length > 0) setOpen(true); }}
            onClose={() => setOpen(false)}

            options={opciones}
            loading={loading}

            // Evitar filtrado local
            filterOptions={(opts) => opts}

            // Cómo "etiquetar" cada opción
            getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                return option.NombreCompleto || '';
            }}

            // Cómo detectar selección (v4)
            isOptionEqualToValue={(opt, val) => opt?.Id === val?.Id}

            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                    setInputValue(newInputValue);
                } else if (reason === 'clear') {
                    setInputValue('');
                    onChange(null);
                    setOpciones([]);
                    setOpen(false);
                } else if (reason === 'reset') {
                    // ignorar
                }
            }}

            onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                    onChange(newValue);
                    setInputValue(newValue.NombreCompleto || '');
                    setOpen(false);
                    setOpciones([]);
                } else {
                    onChange(null);
                    setInputValue('');
                }
            }}

            renderOption={(props, option) => (
                <li {...props} key={option.Id}>
                    <Box py={0.5}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {option.NombreCompleto}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {option.Matricula ? `Mat. Provincial: ${option.Matricula}` : 'Sin matrícula'}
                            {option.Domicilio ? ` • ${option.Domicilio}` : ''}
                        </Typography>
                    </Box>
                </li>
            )}

            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    placeholder={placeholder}
                    fullWidth
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}

            loadingText={
                <Box display="flex" alignItems="center" p={2}>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    <Typography variant="body2">Buscando abogados...</Typography>
                </Box>
            }

            noOptionsText={
                (inputValue || '').trim().length < 3 ? (
                    <Box display="flex" alignItems="center" p={2}>
                        <InfoIcon style={{ marginRight: 8, color: '#1976d2' }} />
                        <Typography variant="body2">Escribe al menos 3 caracteres</Typography>
                    </Box>
                ) : (
                    <Box display="flex" alignItems="center" p={2}>
                        <SearchIcon style={{ marginRight: 8, color: '#999' }} />
                        <Typography variant="body2" color="text.secondary">
                            No se encontraron abogados con matrícula
                        </Typography>
                    </Box>
                )
            }
        />
    );
};

export default BuscadorAbogados;
